<!-- Top header -->
<header
  class="sticky top-0 z-40 flex items-center h-16 gap-4 px-4 bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 sm:px-6 lg:px-8"
>
  <button
    class="lg:hidden p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
    (click)="toggleSidebar()"
  >
    <lucide-icon [img]="Menu" class="w-6 h-6"></lucide-icon>
  </button>

  <div class="lg:hidden flex items-center">
    <img src="/images/miyzan-logo.png" alt="Miyzan" class="h-8 w-auto" />
  </div>

  <div class="flex-1"></div>

  <div class="flex items-center gap-2">
    <!-- ── Feedback Button (Customer/Employee only) ── -->
    @if (currentUser()?.role === 'Customer' || currentUser()?.role === 'Employee') {
      <button
        (click)="openFeedback($event)"
        class="relative p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        [title]="'FEEDBACK.BUTTON_TITLE' | translate"
      >
        <lucide-icon [img]="MessageSquarePlus" class="w-5 h-5"></lucide-icon>
      </button>
    }

    <!-- ── Notification Bell ── -->
    <div class="relative">
      <button
        (click)="togglePanel($event)"
        class="relative p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        [class.bg-gray-100]="panelOpen()"
        [class.dark:bg-gray-800]="panelOpen()"
      >
        <lucide-icon [img]="Bell" class="w-5 h-5"></lucide-icon>
        @if (unreadCount() > 0) {
          <span
            class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 bg-red-500 text-white text-[10px] font-bold rounded-full shadow-sm"
            >{{ unreadCount() }}</span
          >
        }
      </button>

      <!-- ── Slide Panel ── -->
      @if (panelOpen()) {
        <div
          class="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-[4.5rem] sm:top-full mt-0 sm:mt-2 w-auto sm:w-96 max-h-[75vh] sm:max-h-[80vh] flex flex-col rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 overflow-hidden z-50"
          (click)="$event.stopPropagation()"
          (keydown)="$event.stopPropagation()"
          role="region"
          tabindex="0"
          [attr.aria-label]="'ANNOUNCEMENTS.TITLE' | translate"
          style="animation: annSlideIn 200ms cubic-bezier(0.2, 0.8, 0.3, 1) both"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800"
          >
            <div class="flex items-center gap-2.5">
              <div
                class="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center"
              >
                <lucide-icon [img]="Megaphone" class="w-4 h-4 text-white"></lucide-icon>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">
                  {{ 'ANNOUNCEMENTS.TITLE' | translate }}
                </p>
                @if (unreadCount() > 0) {
                  <p class="text-xs text-emerald-600 dark:text-emerald-400">
                    {{ 'ANNOUNCEMENTS.NEW_COUNT' | translate: { count: unreadCount() } }}
                  </p>
                } @else {
                  <p class="text-xs text-gray-400">{{ 'ANNOUNCEMENTS.ALL_READ' | translate }}</p>
                }
              </div>
            </div>
            <button
              (click)="closePanel()"
              class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
            >
              <lucide-icon [img]="X" class="w-4 h-4"></lucide-icon>
            </button>
          </div>

          <!-- List -->
          <div class="overflow-y-auto flex-1">
            @if (announcements().length === 0) {
              <div class="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div
                  class="w-14 h-14 rounded-2xl bg-gray-50 dark:bg-gray-900 flex items-center justify-center mb-3"
                >
                  <lucide-icon
                    [img]="Bell"
                    class="w-7 h-7 text-gray-300 dark:text-gray-600"
                  ></lucide-icon>
                </div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {{ 'ANNOUNCEMENTS.EMPTY' | translate }}
                </p>
                <p class="text-xs text-gray-400 dark:text-gray-600 mt-1">
                  {{ 'ANNOUNCEMENTS.EMPTY_HINT' | translate }}
                </p>
              </div>
            }

            @for (ann of announcements(); track ann.id) {
              <div
                class="group relative flex gap-3 px-5 py-4 border-b border-gray-50 dark:border-gray-800/60 last:border-0 transition-colors"
                [class.bg-white]="isRead(ann.id)"
                [class.dark:bg-gray-950]="isRead(ann.id)"
                [class.bg-emerald-50/30]="!isRead(ann.id)"
                [class.dark:bg-emerald-950/10]="!isRead(ann.id)"
              >
                <!-- Pinned indicator -->
                @if (ann.isPinned) {
                  <div class="absolute top-3 right-4">
                    <lucide-icon
                      [img]="Bookmark"
                      class="w-3.5 h-3.5 text-emerald-400"
                    ></lucide-icon>
                  </div>
                }

                <!-- Type icon -->
                <div
                  class="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mt-0.5"
                  [class]="colorFor(ann.type)"
                >
                  <lucide-icon [img]="iconFor(ann.type)" class="w-4.5 h-4.5"></lucide-icon>
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-start gap-2">
                    <p
                      class="text-sm font-semibold text-gray-900 dark:text-white leading-snug flex-1"
                    >
                      {{ ann.title }}
                    </p>
                    @if (!isRead(ann.id)) {
                      <span
                        class="shrink-0 w-2 h-2 rounded-full mt-1.5"
                        [class]="dotColorFor(ann.type)"
                      ></span>
                    }
                  </div>
                  <p
                    class="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed line-clamp-3"
                  >
                    {{ ann.body }}
                  </p>
                  <div class="flex items-center gap-2 mt-2">
                    <span class="text-[11px] text-gray-400">{{ timeAgo(ann.createdAt) }}</span>
                    @if (ann.createdByName) {
                      <span class="text-[11px] text-gray-300 dark:text-gray-700">·</span>
                      <span class="text-[11px] text-gray-400">{{ ann.createdByName }}</span>
                    }
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- Footer -->
          @if (unreadCount() > 0) {
            <div class="px-5 py-3 border-t border-gray-100 dark:border-gray-800">
              <button
                (click)="markAllRead()"
                class="w-full text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors py-1"
              >
                {{ 'ANNOUNCEMENTS.MARK_ALL_READ' | translate }}
              </button>
            </div>
          }
        </div>
      }
    </div>

    <!-- ── User Menu ── -->
    <div class="relative">
      <button
        (click)="toggleUserMenu($event)"
        class="flex items-center gap-2 p-1.5 pl-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
        [class.bg-gray-100]="userMenuOpen()"
        [class.dark:bg-gray-800]="userMenuOpen()"
      >
        <div
          class="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white flex items-center justify-center text-sm font-semibold"
        >
          {{ userInitials() }}
        </div>
        <span class="hidden sm:inline text-sm font-medium">{{
          currentUser()?.fullName || ('COMMON.USER' | translate)
        }}</span>
        <lucide-icon
          [img]="ChevronDown"
          class="hidden sm:inline w-4 h-4 text-gray-400 transition-transform"
          [class.rotate-180]="userMenuOpen()"
        ></lucide-icon>
      </button>

      @if (userMenuOpen()) {
        <div
          class="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-100 dark:border-gray-800 overflow-hidden z-50"
          (click)="$event.stopPropagation()"
          (keydown)="$event.stopPropagation()"
          role="menu"
          tabindex="0"
          style="animation: annSlideIn 200ms cubic-bezier(0.2, 0.8, 0.3, 1) both"
        >
          <div class="px-3 py-2.5 border-b dark:border-gray-800">
            <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {{ currentUser()?.fullName }}
            </p>
            <p class="text-xs text-gray-400 mt-0.5">
              {{ 'SETTINGS.PROFILE.TITLE' | translate }}
            </p>
          </div>

          <!-- Profile link -->
          <div class="py-1 flex flex-col">
            <a
              routerLink="/app/settings"
              (click)="closeUserMenu()"
              class="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <lucide-icon [img]="User" class="w-4 h-4 text-gray-400"></lucide-icon>
              {{ 'SETTINGS.PROFILE.TITLE' | translate }}
            </a>
          </div>

          <div class="border-t border-gray-100 dark:border-gray-800"></div>

          <!-- Language -->
          <div class="px-4 py-2.5">
            <div class="flex items-center gap-2.5 mb-2">
              <lucide-icon [img]="Languages" class="w-4 h-4 text-gray-400"></lucide-icon>
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{
                'SETTINGS.PREFERENCES.LANGUAGE' | translate
              }}</span>
            </div>
            <div class="grid grid-cols-2 gap-1.5">
              @for (lang of languages; track lang.code) {
                <button
                  (click)="switchLanguage(lang.code)"
                  class="px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors"
                  [class]="
                    languageService.currentLang() === lang.code
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  "
                >
                  {{ lang.label }}
                </button>
              }
            </div>
          </div>

          <div class="border-t border-gray-100 dark:border-gray-800"></div>

          <!-- Theme switch -->
          <button
            (click)="toggleDarkMode()"
            class="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <span class="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300">
              <lucide-icon
                [img]="themeService.isDarkMode() ? Moon : Sun"
                class="w-4 h-4 text-gray-400"
              ></lucide-icon>
              {{ 'SETTINGS.PREFERENCES.DARK_MODE' | translate }}
            </span>
            <span
              class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors"
              [class]="
                themeService.isDarkMode() ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
              "
            >
              <span
                class="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                [class]="themeService.isDarkMode() ? 'translate-x-4' : 'translate-x-0.5'"
              ></span>
            </span>
          </button>

          <div class="border-t border-gray-100 dark:border-gray-800"></div>

          <!--
            Keshni tozalash — qo'llab-quvvatlash uchun oxirgi chora.
            Ikki bosqichli: sarlavha savdo ekranida ham ko'rinadi va bitta
            tasodifiy bosish sahifani yangilab, yig'ilayotgan chekni yo'q qilardi.
          -->
          <div class="py-1">
            <button
              (click)="clearCache()"
              [disabled]="cacheClearing()"
              class="w-full text-left flex items-start gap-2.5 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <lucide-icon
                [img]="Trash2"
                class="w-4 h-4 mt-0.5 shrink-0"
                [class]="cacheClearArmed() ? 'text-amber-500' : 'text-gray-400'"
              ></lucide-icon>
              <span class="flex flex-col">
                <span
                  class="text-sm"
                  [class]="
                    cacheClearArmed()
                      ? 'text-amber-600 dark:text-amber-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300'
                  "
                >
                  {{
                    (cacheClearing()
                      ? 'COMMON.CACHE.CLEARING'
                      : cacheClearArmed()
                        ? 'COMMON.CACHE.CONFIRM'
                        : 'COMMON.CACHE.CLEAR'
                    ) | translate
                  }}
                </span>
                <span class="text-xs text-gray-400 mt-0.5">
                  {{
                    (cacheClearArmed() ? 'COMMON.CACHE.CONFIRM_HINT' : 'COMMON.CACHE.HINT')
                      | translate
                  }}
                </span>
              </span>
            </button>
          </div>

          <div class="border-t border-gray-100 dark:border-gray-800"></div>

          <!-- Logout -->
          <div class="py-1">
            <button
              (click)="handleLogout()"
              class="w-full text-left flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <lucide-icon [img]="LogOut" class="w-4 h-4 mr-2"></lucide-icon>
              {{ 'COMMON.LOGOUT' | translate }}
            </button>
          </div>
        </div>
      }
    </div>
  </div>
</header>
<!-- eslint-disable -->

<!-- ── Feedback Dialog ── -->
@if (feedbackOpen()) {
  <div
    class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
    (click)="closeFeedback()"
  >
    <div
      class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full sm:max-w-md flex flex-col"
      (click)="$event.stopPropagation()"
      (keydown)="$event.stopPropagation()"
      tabindex="0"
      role="dialog"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800"
      >
        <div class="flex items-center gap-2.5">
          <div
            class="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center"
          >
            <lucide-icon [img]="MessageSquarePlus" class="w-4 h-4 text-white"></lucide-icon>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-900 dark:text-white">
              {{ 'FEEDBACK.DIALOG.TITLE' | translate }}
            </p>
            <p class="text-xs text-gray-400">{{ 'FEEDBACK.DIALOG.SUBTITLE' | translate }}</p>
          </div>
        </div>
        <button
          (click)="closeFeedback()"
          class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
        >
          <lucide-icon [img]="X" class="w-4 h-4"></lucide-icon>
        </button>
      </div>

      @if (feedbackSent()) {
        <!-- Success state -->
        <div class="p-8 flex flex-col items-center text-center gap-3">
          <div
            class="w-16 h-16 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center"
          >
            <lucide-icon [img]="Send" class="w-8 h-8 text-emerald-500"></lucide-icon>
          </div>
          <p class="text-base font-semibold text-gray-900 dark:text-white">
            {{ 'FEEDBACK.DIALOG.SUCCESS_TITLE' | translate }}
          </p>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ 'FEEDBACK.DIALOG.SUCCESS_MSG' | translate }}
          </p>
          <button
            (click)="closeFeedback()"
            class="mt-2 px-6 py-2 text-sm bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium"
          >
            {{ 'FEEDBACK.DIALOG.CLOSE' | translate }}
          </button>
        </div>
      } @else {
        <!-- Form -->
        <div class="p-5 space-y-4">
          <!-- Type selector -->
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              {{ 'FEEDBACK.DIALOG.TYPE_LABEL' | translate }}
            </p>
            <div class="grid grid-cols-3 gap-2">
              @for (ft of feedbackTypes; track ft.type) {
                <button
                  (click)="feedbackType.set(ft.type)"
                  class="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all text-xs font-medium"
                  [class]="
                    feedbackType() === ft.type
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                  "
                >
                  <span
                    class="w-8 h-8 rounded-lg flex items-center justify-center"
                    [class]="ft.color"
                  >
                    <lucide-icon [img]="ft.icon" class="w-4 h-4"></lucide-icon>
                  </span>
                  {{ ft.label | translate }}
                </button>
              }
            </div>
          </div>

          <!-- Message -->
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              {{ 'FEEDBACK.DIALOG.MESSAGE_LABEL' | translate }}
            </p>
            <textarea
              [ngModel]="feedbackMessage()"
              (ngModelChange)="feedbackMessage.set($event)"
              rows="4"
              [placeholder]="'FEEDBACK.DIALOG.PLACEHOLDER' | translate"
              class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            ></textarea>
          </div>

          <button
            (click)="submitFeedback()"
            [disabled]="!feedbackMessage().trim() || feedbackSubmitting()"
            class="w-full flex items-center justify-center gap-2 py-2.5 text-sm bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors font-medium"
          >
            <lucide-icon [img]="Send" class="w-4 h-4"></lucide-icon>
            {{
              (feedbackSubmitting() ? 'FEEDBACK.DIALOG.SUBMITTING' : 'FEEDBACK.DIALOG.SUBMIT')
                | translate
            }}
          </button>
        </div>
      }
    </div>
  </div>
}

<style>
  @keyframes annSlideIn {
    from {
      opacity: 0;
      transform: translateY(-8px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
</style>
